import React, { useState, useEffect, useCallback, useContext } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_ITEM_MOVING_REPORT, GET_DOCTORS, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
// Build initial filters
const buildInitialFilters = (filters = []) => {
    const today = new Date();

    return filters.reduce((accumulator, filter) => {
        if (filter.type === 'date') {
            const dateValue = formatLocalDate(today);
            return { ...accumulator, [filter.id]: dateValue };
        }

        if (filter.type === 'select') {
            const defaultValue = filter.options?.[0]?.value ?? '';
            return { ...accumulator, [filter.id]: defaultValue };
        }

        return { ...accumulator, [filter.id]: '' };
    }, {});
};

// Report configuration
const reportConfig = {
    filters: [
        { id: 'fromDate', type: 'date', label: 'From Date' },
        { id: 'toDate', type: 'date', label: 'To Date' },
        { id: 'refDoctorId', type: 'doctor', label: 'Doctor' },
    ],
    table: {
        columns: [
            { key: 'ItemName', label: 'Item Name' },
            { key: 'SalesQty', label: 'Sales Qty' },
            { key: 'ReturnQty', label: 'Return Qty' },
            { key: 'NetSoldQty', label: 'Net Sold Qty' },
            { key: 'ItemOnhand', label: 'On Hand' },
        ],
    },
};

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '38px',
        height: '38px',
        backgroundColor: '#f9fafb',
        borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
        boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
        borderRadius: '0.5rem',
        '&:hover': {
            borderColor: '#6366f1',
        },
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '2px 8px',
        height: '38px',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '38px',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? '#92c3fcff'
            : state.isFocused
                ? '#92c3fcff'
                : '#ffffff',
        color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
        fontWeight: '500',
        padding: '6px 8px',
        '&:hover': {
            backgroundColor: '#92c3fcff',
            color: '#000000ff',
        },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ItemMovingReport = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [reportData, setReportData] = useState([]);
    const token = localStorage.getItem('accessToken');
    const [showExportModal, setShowExportModal] = useState(false);
    const [doctors, setDoctors] = useState([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);

    useEffect(() => {
        const getDoctors = async () => {
            try {
                const response = await axios.get(`${BASE_URL}${GET_DOCTORS}`, {
                    headers: {
                        'XApiKey': process.env.REACT_APP_API_KEY,
                        'Authorization': `Bearer ${token}`
                    },
                });

                if (response.status === 200) {
                    setDoctors(response.data.data || []);
                } else {
                    setDoctors([]);
                }
            } catch (error) {
                console.error('Failed to fetch doctors data:', error);
                setDoctors([]);
            }
        };

        getDoctors();
    }, [token]);

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getItemMovingReport();
    };

    const handleExport = () => {
        if (reportData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getItemMovingReport = useCallback(async () => {
        setLoading(true);
        try {
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;

            const response = await axios.get(`${BASE_URL}${GET_ITEM_MOVING_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'refDoctorId': filters.refDoctorId || null,
                },
            });

            if (response.status === 200 && response.data?.isSucess) {
                const data = response.data.data || [];
                // Sort by SalesQty in descending order
                const sortedData = data.sort((a, b) => {
                    const salesQtyA = parseFloat(a.SalesQty || 0);
                    const salesQtyB = parseFloat(b.SalesQty || 0);
                    return salesQtyB - salesQtyA; // Descending order
                });
                setReportData(sortedData);
                toast.success('Report generated successfully', { autoClose: 1000 });
            } else {
                toast.error('Failed to fetch report data', { autoClose: 2000 });
                setReportData([]);
            }
        } catch (error) {
            console.error('Failed to fetch item moving report:', error);
            toast.error('Failed to fetch report data', { autoClose: 2000 });
            setReportData([]);
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    const exportToExcel = () => {
        try {
            const excelData = reportData.map((row, index) => ({
                'Sl.': index + 1,
                'Item Name': row.ItemName || '',
                'Sales Qty': Number(parseFloat(row.SalesQty || 0).toFixed(2)),
                'Return Qty': Number(parseFloat(row.ReturnQty || 0).toFixed(2)),
                'Net Sold Qty': Number(parseFloat(row.NetSoldQty || 0).toFixed(2)),
                'On Hand': Number(parseFloat(row.ItemOnhand || 0).toFixed(2)),
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 6 },   // Sl.
                { wch: 40 },  // Item Name
                { wch: 12 },  // Sales Qty
                { wch: 12 },  // Return Qty
                { wch: 15 },  // Net Sold Qty
                { wch: 12 },  // On Hand
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Item Moving Report');

            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Item_Moving_Report_${fromDateStr}_to_${toDateStr}.xlsx`;

            XLSX.writeFile(wb, filename);
            toast.success(`Excel file exported successfully with ${reportData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');

            // Add title
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text('Item Moving Report', 14, 15);

            // Add date range
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Period: ${fromDateStr} to ${toDateStr}`, 14, 22);

            // Prepare table data
            const tableData = reportData.map((row, index) => [
                index + 1,
                row.ItemName || '',
                parseFloat(row.SalesQty || 0).toFixed(2),
                parseFloat(row.ReturnQty || 0).toFixed(2),
                parseFloat(row.NetSoldQty || 0).toFixed(2),
                parseFloat(row.ItemOnhand || 0).toFixed(2),
            ]);

            // Add table
            autoTable(doc, {
                head: [['Sl.', 'Item Name', 'Sales Qty', 'Return Qty', 'Net Sold Qty', 'On Hand']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
                columnStyles: {
                    0: { cellWidth: 15 },
                    1: { cellWidth: 90 },
                    2: { cellWidth: 28, halign: 'right' },
                    3: { cellWidth: 28, halign: 'right' },
                    4: { cellWidth: 32, halign: 'right' },
                    5: { cellWidth: 28, halign: 'right' },
                },
                margin: { left: 14, right: 14 },
            });

            const filename = `Item_Moving_Report_${fromDateStr.replace(/\//g, '-')}_to_${toDateStr.replace(/\//g, '-')}.pdf`;
            doc.save(filename);
            toast.success('PDF exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    // Pagination calculations
    const totalPages = Math.ceil(reportData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = reportData.slice(startIndex, endIndex);

    // Reset to page 1 when filters change or new data is loaded
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate, reportData.length]);

    // Calculate totals
    const totals = {
        salesQty: reportData.reduce((sum, item) => sum + parseFloat(item.SalesQty || 0), 0),
        returnQty: reportData.reduce((sum, item) => sum + parseFloat(item.ReturnQty || 0), 0),
        netSoldQty: reportData.reduce((sum, item) => sum + parseFloat(item.NetSoldQty || 0), 0),
        ItemOnhand: reportData.reduce((sum, item) => sum + parseFloat(item.ItemOnhand || 0), 0),
    };

    const doctorsOptions = doctors.map((doctor) => ({
        value: doctor.doctorID,
        label: doctor.designation
            ? `${doctor.doctorName}, ${doctor.designation}`
            : doctor.doctorName,
        DoctorName: doctor.doctorName,
        Designation: doctor.designation,
    }));

    const customDoctorLabel = ({ DoctorName, Designation }) => (
        <div className="flex items-center space-x-3 py-1">
            {DoctorName && (
                <div className="font-semibold text-xs text-black">
                    {DoctorName}
                </div>
            )}
            {Designation && (
                <div className="font-semibold text-xs text-black">
                    , {Designation}
                </div>
            )}
        </div>
    );

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Sales Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Item Moving Report
                                    </h1>
                                </div>
                            )}
                            <form className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'doctor') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                                {filter.label}
                                                <Select
                                                    options={doctorsOptions}
                                                    value={doctorsOptions.find(opt => opt.value === filters[filter.id]) || null}
                                                    onChange={(selected) => handleFilterChange(filter.id, selected ? selected.value : 0)}
                                                    placeholder="Select Doctor"
                                                    isClearable
                                                    styles={customSelectStyles}
                                                    formatOptionLabel={customDoctorLabel}
                                                    menuPortalTarget={document.body}
                                                />
                                            </label>
                                        );
                                    }

                                    return null;
                                })}
                            </form>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Run report
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                    setReportData([]);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={reportData.length === 0}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-6 flex-1 min-h-0">
                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-1">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, reportData.length)}</span> of <span className="font-semibold text-slate-700">{reportData.length}</span> items for this report.
                                    </p>
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {reportData.length > 0 ? (
                                            paginatedData.map((row, index) => (
                                                <tr key={`${row.ItemNo}-${index}`} className="hover:bg-indigo-50/40 transition">
                                                    {reportConfig.table.columns.map((column) => {
                                                        let cellValue = row[column.key];

                                                        // Format numeric columns
                                                        if (['ItemOnhand', 'SalesQty', 'ReturnQty', 'NetSoldQty'].includes(column.key)) {
                                                            cellValue = parseFloat(cellValue || 0).toFixed(2);
                                                        }

                                                        return (
                                                            <td key={column.key} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                                                {cellValue}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length} className="px-4 py-8 text-center text-slate-500">
                                                    No data available for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {/* Bottom Pagination Controls */}
                            {reportData.length > 0 && totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-200">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 7) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 6 + i;
                                            } else {
                                                pageNum = currentPage - 3 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="text-sm text-slate-600 px-2">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>

            {/* Export Format Modal */}
            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={(format) => {
                    if (format === 'excel') {
                        exportToExcel();
                    } else if (format === 'pdf') {
                        exportToPDF();
                    }
                }}
            />
        </div>
    );
};

export default withAuth(ItemMovingReport);
